import { distinctBy } from "@entrilia/shared/utilities/arrayHelper.ts";
import { validateCondition } from "../../../store/utils/fieldsUtils.ts";
import { chartRelatedReportTypes } from "../../../utilities/chartRelatedReport.types.ts";
import {
  getChartDimensionNames,
  getFinancialStatementDimensionNames,
  getListDimensionNames,
  getPivotDimensionNames,
  getTabularDimensionNames,
  getTileDimensionNames,
} from "../../../utilities/getDimensionNames.ts";
import {
  getChartMeasureNames,
  getFinancialStatementMeasureNames,
  getPivotMeasureNames,
  getTabularMeasureNames,
  getTileMeasureNames,
} from "../../../utilities/getMeasureNames.ts";
import { usesTabularConfiguration } from "../../../utilities/reportTypeHelper.ts";
import type {
  ChartRunState,
  FinancialStatementRunState,
  ListRunState,
  PivotRunState,
  ReportConfigurationForRunning,
  RunStateAction,
  RunStateBase,
  TabularRunState,
  TileRunState,
} from "./runState.ts";
import type {
  DimensionDescriptor,
  ReportField,
  RunReportConfigurationBase,
  StatementLineConfiguration,
} from "../../../../../../../api/types/svTypes.ts";
import { DimensionFieldType, ReportType } from "../../../../../../../api/types/svTypes.ts";
import { type GetFieldCaption, getLineLabel } from "../financialStatement/statementGridModel.ts";

export const isTileRunState = (runState: ReportConfigurationForRunning): runState is TileRunState => {
  return runState.reportType === ReportType.Tile;
};

export const isTabularRunState = (runState: ReportConfigurationForRunning): runState is TabularRunState => {
  return usesTabularConfiguration(runState.reportType);
};

export const isPivotRunState = (runState: ReportConfigurationForRunning): runState is PivotRunState => {
  return runState.reportType === ReportType.Pivot;
};

export const isChartRunState = (runState: ReportConfigurationForRunning): runState is ChartRunState => {
  return chartRelatedReportTypes.includes(runState.reportType);
};

export const isListRunState = (runState: ReportConfigurationForRunning): runState is ListRunState => {
  return runState.reportType === ReportType.List;
};

export const isFinancialStatementRunState = (
  runState: ReportConfigurationForRunning
): runState is FinancialStatementRunState => {
  return runState.reportType === ReportType.FinancialStatement;
};

export const getStatementLineFieldCaption = (
  field: ReportField,
  lines: StatementLineConfiguration[],
  getFieldCaption: GetFieldCaption
): string => {
  const line = lines.find((l) => l.field?.guid === field.config.guid);
  return line ? getLineLabel(line, getFieldCaption) : field.meta.caption;
};

export const initializeCommonRunState = (
  action: Extract<RunStateAction, { type: "Initialize" }>
): RunReportConfigurationBase & RunStateBase => {
  const response = action.payload.response;
  const conditions = response.conditions.map((condition) => ({
    ...condition,
    validation: validateCondition(condition, response.conditions, response.reportType),
  }));
  return {
    dataSetId: action.payload.response.dataSetId,
    reportType: action.payload.response.reportType,
    conditions,
    showParameters: action.payload.response.showParameters,
    fieldIdsWithCustomConditionParameters: action.payload.response.fieldIdsWithCustomConditionParameters,
    books: action.payload.response.books,
    isMultiBook: action.payload.response.isMultiBook,
    customConditionsMeta: action.payload.response.customConditionsMeta,
    refreshToken: "",
    dimensionNames: getDimensionNames(action.payload.response),
    measureNames: getMeasureNames(action.payload.response),
    fieldsWithCustomConditionParameters: getFieldsWithCustomConditionParameters({ ...response, conditions }),
    dimensions: getDimensionsFromReportConfiguration(action.payload.response),
  };
};

const getFieldsWithCustomConditionParameters = (config: ReportConfigurationForRunning): ReportField[] => {
  const linkedParameterIds = new Set(
    config.conditions
      .filter((condition) => condition.config.parameter && !condition.validation?.invalid)
      .map((condition) => condition.config.guid)
  );
  const hasConditionParameters = (field: ReportField) =>
    config.fieldIdsWithCustomConditionParameters.includes(field.config.guid) ||
    (field.config.useCustomConditions && field.config.linkedConditions?.some((id) => linkedParameterIds.has(id)));

  if (isTabularRunState(config) || isTileRunState(config) || isListRunState(config)) {
    return config.fields.filter(hasConditionParameters);
  }
  if (isPivotRunState(config) || isChartRunState(config)) {
    return config.values.filter(hasConditionParameters);
  }
  if (isFinancialStatementRunState(config)) {
    return config.lineFields.filter(hasConditionParameters);
  }
  throw new Error("Invalid report type");
};

const getDimensionsFromReportConfiguration = (config: ReportConfigurationForRunning): DimensionDescriptor[] => {
  return distinctBy(
    config.conditions
      .map((c) => c.meta)
      .concat(config.customConditionsMeta)
      .concat(getDimensionsFromFields(config)),
    (d) => d.name
  );
};

const getDimensionsFromFields = (config: ReportConfigurationForRunning) => {
  if (isTabularRunState(config) || isTileRunState(config) || isListRunState(config)) {
    return config.fields.filter((m) => m.type === DimensionFieldType.DIMENSION).map((f) => f.meta);
  }
  if (isPivotRunState(config)) {
    return config.rows
      .concat(config.columns)
      .concat(config.values.filter((v) => v.type === DimensionFieldType.DIMENSION))
      .map((r) => r.meta);
  }
  if (isChartRunState(config)) {
    return config.arguments
      .concat(config.values.filter((v) => v.type === DimensionFieldType.DIMENSION))
      .map((r) => r.meta);
  }
  if (isFinancialStatementRunState(config)) {
    return config.dimensionsMeta;
  }
  throw new Error("Invalid report type");
};

const getMeasureNames = (config: ReportConfigurationForRunning): string[] => {
  if (isTabularRunState(config)) {
    return getTabularMeasureNames(config.fields.map((f) => f.config));
  }
  if (isPivotRunState(config)) {
    return getPivotMeasureNames(config.values.map((v) => v.config));
  }
  if (isChartRunState(config)) {
    return getChartMeasureNames(config.values.map((v) => v.config));
  }
  if (isTileRunState(config)) {
    return getTileMeasureNames(config.fields.map((f) => f.config));
  }
  if (isListRunState(config)) {
    return [];
  }
  if (isFinancialStatementRunState(config)) {
    return getFinancialStatementMeasureNames(config.lines);
  }
  throw new Error("Invalid report type");
};

const getDimensionNames = (config: ReportConfigurationForRunning): string[] => {
  if (isTabularRunState(config)) {
    return getTabularDimensionNames(
      config.fields.map((f) => f.config),
      config.conditions.map((c) => c.config)
    );
  }

  if (isPivotRunState(config)) {
    return getPivotDimensionNames(
      config.conditions.map((c) => c.config),
      config.rows.map((r) => r.config),
      config.columns.map((c) => c.config)
    );
  }
  if (isChartRunState(config)) {
    return getChartDimensionNames(
      config.conditions.map((c) => c.config),
      config.arguments.map((a) => a.config)
    );
  }
  if (isTileRunState(config)) {
    return getTileDimensionNames(
      config.fields.map((f) => f.config),
      config.conditions.map((c) => c.config)
    );
  }
  if (isListRunState(config)) {
    return getListDimensionNames(
      config.fields.map((f) => f.config),
      config.conditions.map((c) => c.config)
    );
  }
  if (isFinancialStatementRunState(config)) {
    return getFinancialStatementDimensionNames(
      config.conditions.map((c) => c.config),
      config.lines,
      config.columns
    );
  }
  throw new Error("Invalid report type");
};
